"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { Section } from "@/components/ui/section";
import { CommunityFilterBar } from "./CommunityFilterBar";
import { CommunitySearchResults } from "./CommunitySearchResults";
import { useCommunitySearch } from "./useCommunitySearch";
import { buildCommunitySearchHref } from "@/lib/community/search-params";
import type { QuickFilterValue } from "./QuickFilterChips";
import type { CountryCode } from "./CountryFilterChips";
import type { RegionCode } from "@/lib/community/constants";
import type {
  PublicCommunityFilterOptionsResponse,
  PublicCommunityShopsSearchQuery,
} from "@/lib/community/types";

type SortBy = NonNullable<
  PublicCommunityShopsSearchQuery["filter_sort_by"]
>;

const VALID_SORTS: SortBy[] = [
  "default",
  "newest",
  "trending",
  "popularity",
];

const VALID_QUICK_FILTERS: QuickFilterValue[] = [
  "newest",
  "trending",
  "popularity",
];

const PAGE_SIZE = 10;

interface CommunitySearchPageProps {
  browse: PublicCommunityFilterOptionsResponse;
}

export function CommunitySearchPage({ browse }: CommunitySearchPageProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const searchQuery = searchParams.get("q") ?? "";
  const [searchInput, setSearchInput] = useState(searchQuery);

  const rawSortBy =
    searchParams.get("sort") ?? searchParams.get("quick") ?? "default";
  const sortBy: SortBy = VALID_SORTS.includes(rawSortBy as SortBy)
    ? (rawSortBy as SortBy)
    : "default";

  const quickFilter: QuickFilterValue = VALID_QUICK_FILTERS.includes(
    sortBy as QuickFilterValue,
  )
    ? (sortBy as QuickFilterValue)
    : null;

  const selectedRegion = searchParams.get("region") as RegionCode | null;
  const selectedCountry = searchParams.get("country") as CountryCode | null;
  const selectedCategory = searchParams.get("category");
  const selectedCollection = searchParams.get("collection");

  const resultsRef = useRef<HTMLDivElement>(null);

  const setParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      router.replace(
        buildCommunitySearchHref(pathname, searchParams.toString(), updates),
        { scroll: false },
      );
    },
    [router, pathname, searchParams],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Restore the input draft when browser navigation changes the URL query.
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (searchInput === searchQuery) return;
    const timer = setTimeout(() => {
      setParams({ q: searchInput || null });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, setParams]);

  const query: PublicCommunityShopsSearchQuery = useMemo(() => {
    const shipsFrom = selectedCountry ?? selectedRegion ?? undefined;

    return {
      search_query: searchQuery || undefined,
      filter_category_internal_id: selectedCategory ?? undefined,
      filter_featured_collection_internal_id: selectedCollection ?? undefined,
      filter_ships_from: shipsFrom,
      filter_sort_by: quickFilter ?? sortBy,
      take: PAGE_SIZE,
      skip: 0,
    };
  }, [
    searchQuery,
    sortBy,
    selectedRegion,
    selectedCountry,
    selectedCategory,
    selectedCollection,
    quickFilter,
  ]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    isFetchNextPageError,
  } = useCommunitySearch(query);

  const shops = useMemo(
    () => data?.pages.flatMap((page) => page.shops) ?? [],
    [data],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const handleQuickFilterChange = (value: QuickFilterValue) => {
    if (value === null) {
      setParams({ quick: null, sort: "default" });
    } else {
      setParams({ quick: null, sort: value });
    }
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleBasedInChange = (
    type: "region" | "country" | null,
    code: RegionCode | CountryCode | null,
  ) => {
    if (type === "region") {
      setParams({ region: code, country: null });
    } else if (type === "country") {
      setParams({ country: code, region: null });
    } else {
      setParams({ region: null, country: null });
    }
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCategoryChange = (id: string | null) => {
    setParams({ category: id });
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCollectionChange = (id: string | null) => {
    setParams({ collection: id });
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleClearFilters = () => {
    setSearchInput("");
    router.replace(pathname);
  };

  return (
    <>
      <Section background="gradient" size="sm" className="py-6 md:py-8">
        <CommunityFilterBar
          searchQuery={searchInput}
          quickFilter={quickFilter}
          selectedRegion={selectedRegion}
          selectedCountry={selectedCountry}
          selectedCategory={selectedCategory}
          selectedCollection={selectedCollection}
          categories={browse.categories}
          collections={browse.collections}
          regions={browse.regions}
          countries={browse.countries}
          onSearchChange={handleSearchChange}
          onQuickFilterChange={handleQuickFilterChange}
          onBasedInChange={handleBasedInChange}
          onCategoryChange={handleCategoryChange}
          onCollectionChange={handleCollectionChange}
          onClearFilters={handleClearFilters}
          showActivePills={false}
        />
      </Section>

      <Section background="white" size="sm" className="pt-8 md:pt-10">
        <CommunitySearchResults
          ref={resultsRef}
          shops={shops}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          error={error}
          onRetry={isFetchNextPageError ? fetchNextPage : refetch}
          onClearFilters={handleClearFilters}
          returnTo={buildCommunitySearchHref(pathname, searchParams.toString(), { sort: sortBy })}
        />
      </Section>
    </>
  );
}
